import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { RotateCcw, Check, X, Layers, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MODES = [
  { id: 'alle', label: 'Alle Karten' },
  { id: 'falsch', label: 'Nur falsche' },
  { id: 'neu', label: 'Noch nicht gelernt' },
];

export default function FlashcardTrainerPage() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [courseFilter, setCourseFilter] = useState('all');
  const [mode, setMode] = useState('alle');
  const [deck, setDeck] = useState([]);
  const [pos, setPos] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [sessionStats, setSessionStats] = useState({ known: 0, wrong: 0 });
  const [running, setRunning] = useState(false);

  const load = () => axios.get(`${API}/api/learning/flashcards`, getAuthHeader())
    .then((r) => setData(r.data)).catch(() => setData({ cards: [], courses: [], stats: {} }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    let cards = data.cards;
    if (courseFilter !== 'all') cards = cards.filter((c) => c.course_id === courseFilter);
    if (mode === 'falsch') cards = cards.filter((c) => c.seen && !c.known);
    if (mode === 'neu') cards = cards.filter((c) => !c.seen);
    return cards;
  }, [data, courseFilter, mode]);

  const start = () => {
    // Reihenfolge: erst falsche, dann neue, dann gewusste — innerhalb gemischt
    const wrong = filtered.filter((c) => c.seen && !c.known);
    const fresh = filtered.filter((c) => !c.seen);
    const known = filtered.filter((c) => c.known);
    const shuffle = (a) => a.map((x) => [Math.random(), x]).sort((p, q) => p[0] - q[0]).map(([, x]) => x);
    setDeck([...shuffle(wrong), ...shuffle(fresh), ...shuffle(known)]);
    setPos(0); setFlipped(false); setSessionStats({ known: 0, wrong: 0 }); setRunning(true);
  };

  const answer = async (known) => {
    const card = deck[pos];
    setSessionStats((s) => ({ known: s.known + (known ? 1 : 0), wrong: s.wrong + (known ? 0 : 1) }));
    axios.post(`${API}/api/learning/flashcards/result`, { card_id: card.id, known }, getAuthHeader()).catch(() => {});
    setFlipped(false);
    setPos((p) => p + 1);
  };

  if (!data) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-7 h-7 animate-spin text-terra" /></div>;
  }

  const stats = data.stats || {};
  const card = running ? deck[pos] : null;
  const finished = running && pos >= deck.length;

  return (
    <div className="max-w-3xl mx-auto animate-fade-in" data-testid="flashcard-trainer-page">
      <div className="mb-8">
        <p className="text-[10.5px] uppercase font-bold mb-1" style={{ letterSpacing: '0.26em', color: '#8F650F' }}>
          Wiederholen & festigen
        </p>
        <h1 className="font-display text-4xl sm:text-5xl text-espresso">Lernkarten<span className="italic text-terra">-Trainer</span>.</h1>
        <p className="text-[14px] text-sand-600 mt-2 leading-relaxed">
          Alle Lernkarten aus deinen Kursen — Karten, die du nicht wusstest, kommen zuerst wieder dran.
        </p>
      </div>

      {stats.total === 0 ? (
        <div className="bg-cream-100 border border-cream-300 rounded-2xl p-10 text-center" data-testid="flashcards-empty">
          <Layers className="w-8 h-8 mx-auto mb-3 text-sand-400" />
          <p className="text-[14px] text-sand-600">Für deine Kurse gibt es noch keine Lernkarten — sie erscheinen automatisch, sobald das Vertiefungsmaterial deiner Module bereitsteht.</p>
        </div>
      ) : !running ? (
        <div className="bg-cream-100 border border-cream-300 rounded-2xl p-8" data-testid="flashcards-setup">
          <div className="grid grid-cols-3 gap-3 mb-7">
            {[['Gewusst', stats.known, '#4E6B3E'], ['Nicht gewusst', stats.wrong, '#A83232'], ['Neu', stats.unseen, '#8F650F']].map(([l, v, col]) => (
              <div key={l} className="dh-frame text-center !p-1.5">
                <div className="dh-mat px-3 py-2.5">
                  <p className="font-display text-[26px]" style={{ color: col }}>{v || 0}</p>
                  <p className="text-[10.5px] uppercase font-bold text-sand-500" style={{ letterSpacing: '0.18em' }}>{l}</p>
                </div>
              </div>
            ))}
          </div>
          {data.courses.length > 1 && (
            <select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}
              className="w-full mb-4 px-4 py-3 bg-white border border-cream-300 rounded-xl text-[14px]" data-testid="flashcards-course-select">
              <option value="all">Alle Kurse ({data.cards.length} Karten)</option>
              {data.courses.map((c) => (
                <option key={c.id} value={c.id}>{`${c.title} (${data.cards.filter((x) => x.course_id === c.id).length} Karten)`}</option>
              ))}
            </select>
          )}
          <div className="flex gap-2 mb-7 flex-wrap">
            {MODES.map((m) => (
              <button key={m.id} onClick={() => setMode(m.id)}
                className="px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors"
                style={mode === m.id ? { backgroundColor: '#E07A5F', color: '#F5F0EA' } : { border: '1px solid #DACFBE', color: '#7A6A58' }}
                data-testid={`flashcards-mode-${m.id}`}>
                {m.label}
              </button>
            ))}
          </div>
          <button onClick={start} disabled={filtered.length === 0}
            className="w-full py-4 rounded-full text-[13px] font-bold uppercase tracking-[0.18em] disabled:opacity-40"
            style={{ backgroundColor: '#E07A5F', color: '#F5F0EA' }} data-testid="flashcards-start-btn">
            {filtered.length === 0 ? 'Keine Karten in dieser Auswahl' : `${filtered.length} Karten trainieren`}
          </button>
        </div>
      ) : finished ? (
        <div className="bg-cream-100 border border-cream-300 rounded-2xl p-10 text-center" data-testid="flashcards-done">
          <div className="mx-auto mb-4" style={{ width: 60, height: 1, backgroundColor: '#D89232' }} />
          <h2 className="font-display text-[30px] text-espresso mb-2">Runde <span className="italic text-terra">geschafft</span>.</h2>
          <p className="text-[14.5px] text-sand-600 mb-7">
            <span className="font-bold" style={{ color: '#4E6B3E' }}>{sessionStats.known} gewusst</span> · <span className="font-bold" style={{ color: '#A83232' }}>{sessionStats.wrong} nicht gewusst</span>
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            {sessionStats.wrong > 0 && (
              <button onClick={() => { setMode('falsch'); setRunning(false); load(); }}
                className="px-7 py-3 rounded-full text-[11.5px] font-bold uppercase tracking-widest"
                style={{ backgroundColor: '#E07A5F', color: '#F5F0EA' }} data-testid="flashcards-repeat-wrong">
                Falsche wiederholen
              </button>
            )}
            <button onClick={() => { setRunning(false); load(); }}
              className="px-7 py-3 rounded-full text-[11.5px] font-bold uppercase tracking-widest"
              style={{ border: '1.5px solid #E07A5F', color: '#E07A5F' }} data-testid="flashcards-back-to-start">
              Zur Übersicht
            </button>
          </div>
        </div>
      ) : (
        <div data-testid="flashcards-session">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[12px] font-bold uppercase tracking-widest text-sand-500" data-testid="flashcards-progress">
              Karte {pos + 1} von {deck.length}
            </p>
            <p className="text-[12px] text-sand-500">{card.module_title}</p>
          </div>
          <div className="h-1 rounded-full bg-cream-300 mb-6 overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${(pos / deck.length) * 100}%`, backgroundColor: '#E07A5F' }} />
          </div>
          <div className="dh-frame mb-5">
            <button onClick={() => setFlipped(!flipped)}
              className="w-full text-left transition-colors dh-mat"
              style={{ backgroundColor: flipped ? '#3A2A22' : '#FBF6F0', borderColor: flipped ? '#3A2A22' : undefined, minHeight: 220 }}
              data-testid="flashcard-active">
              <div className="p-6 sm:p-8">
                <p className="text-[10px] uppercase font-bold mb-3 flex items-center gap-1.5"
                  style={{ letterSpacing: '0.24em', color: flipped ? '#D89232' : '#8F650F' }}>
                  <RotateCcw className="w-3 h-3" /> {flipped ? 'Antwort' : 'Frage — tippen zum Umdrehen'}
                </p>
                <p className="font-display text-[21px] leading-relaxed" style={{ color: flipped ? '#F5F0EA' : '#3A2A22' }}>
                  {flipped ? card.back : card.front}
                </p>
              </div>
            </button>
          </div>
          {flipped ? (
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => answer(false)}
                className="py-4 rounded-full text-[12px] font-bold uppercase tracking-widest flex items-center justify-center gap-2"
                style={{ border: '1.5px solid #A83232', color: '#A83232' }} data-testid="flashcard-wrong-btn">
                <X className="w-4 h-4" /> Nicht gewusst
              </button>
              <button onClick={() => answer(true)}
                className="py-4 rounded-full text-[12px] font-bold uppercase tracking-widest flex items-center justify-center gap-2"
                style={{ backgroundColor: '#4E6B3E', color: '#F5F0EA' }} data-testid="flashcard-known-btn">
                <Check className="w-4 h-4" /> Gewusst
              </button>
            </div>
          ) : (
            <p className="text-center text-[12.5px] text-sand-500 italic">Erst überlegen, dann Karte umdrehen.</p>
          )}
        </div>
      )}
    </div>
  );
}
